<template>
  <div
    class="outlook-card"
    :class="{ 'outlook-card--connected': isConnected }"
    @click="handleOpen"
  >
    <div class="outlook-card__top">
      <div class="outlook-card__brand">
        <div class="outlook-card__logo" aria-hidden="true">
          <img :src="iconSrc" :alt="productLabel" />
        </div>
        <div class="outlook-card__heading">
          <h3 class="outlook-card__title">{{ title }}</h3>
          <p class="outlook-card__description">
            {{ description }}
          </p>
        </div>
      </div>

      <div class="outlook-card__status" :class="statusClass">
        <span>{{ statusLabel }}</span>
      </div>
    </div>

    <div v-if="isConnected && !hasCalendarSelected" class="outlook-card__banner">
      Koppel ook een agenda om beschikbaarheid en slots correct te synchroniseren.
    </div>

    <div class="outlook-card__footer">
      <span class="outlook-card__meta">
        {{ calendarLabel }}
      </span>
      <Btn
        :variant="isConnected ? 'tertiary' : 'primary'"
        size="sm"
        type="button"
        :loading="loading"
        @click.stop="handlePrimaryAction"
      >
        {{ actionLabel }}
      </Btn>
    </div>

    <div v-if="showAdminConsent && provider === 'outlook'" class="outlook-card__consent" @click.stop>
      <p class="outlook-card__consent-hint">
        Externe Microsoft 365-organisatie: de beheerder opent deze link één keer.
      </p>
      <Btn
        variant="outlined"
        size="sm"
        type="button"
        :loading="copyingAdminConsent"
        @click.stop="handleCopyAdminConsent"
      >
        Kopieer admin consent link
      </Btn>
    </div>
  </div>
</template>

<script>
  import { computed } from 'vue';
  import Btn from '@/components/partials/Btn.vue';

  export default {
    name: 'OutlookIntegrationCard',
    components: { Btn },
    props: {
      provider: {
        type: String,
        default: 'outlook',
        validator: (value) => ['outlook', 'google'].includes(value),
      },
      integration: {
        type: Object,
        default: null,
      },
      loading: {
        type: Boolean,
        default: false,
      },
      copyingAdminConsent: {
        type: Boolean,
        default: false,
      },
      showAdminConsent: {
        type: Boolean,
        default: true,
      },
      addingAnother: {
        type: Boolean,
        default: false,
      },
    },
    emits: ['connect', 'manage', 'copy-admin-consent'],
    setup(props, { emit }) {
      const icons = {
        google: new URL('../../assets/img/integrations/google_calendar.png', import.meta.url).href,
        outlook: new URL('../../assets/img/integrations/outlook.png', import.meta.url).href,
      };
      const isGoogle = computed(() => props.provider === 'google');
      const productLabel = computed(() => (isGoogle.value ? 'Google Calendar' : 'Outlook Calendar'));
      const providerLabel = computed(() => (isGoogle.value ? 'Google' : 'Outlook'));
      const iconSrc = computed(() => icons[props.provider] || icons.outlook);
      const isConnected = computed(() => !!props.integration);
      // The integrations list omits settings. Only warn once the detail payload is loaded.
      const hasCalendarSelected = computed(() => {
        const settings = props.integration?.settings;
        if (!settings) return true;
        return !!settings.calendar_id;
      });

      const title = computed(() => {
        if (!isConnected.value && props.addingAnother) {
          return `Nog een ${providerLabel.value}-account`;
        }
        return props.integration?.name || productLabel.value;
      });

      const description = computed(() => {
        if (!isConnected.value && props.addingAnother) {
          return (
            `Koppel een extra ${providerLabel.value}-agenda, ` +
            'naast de accounts die je al hebt.'
          );
        }
        if (!isConnected.value) {
          return isGoogle.value
            ? 'Synchroniseer afspraken met je Google-agenda.'
            : 'Synchroniseer afspraken met je Microsoft-agenda.';
        }
        return `Synchroniseer afspraken met deze ${providerLabel.value}-agenda.`;
      });

      const actionLabel = computed(() => {
        if (isConnected.value) return `Beheer ${providerLabel.value}`;
        if (props.addingAnother) return 'Account toevoegen';
        return `Verbind ${providerLabel.value}`;
      });

      const statusLabel = computed(() => {
        if (!isConnected.value && props.addingAnother) return 'Toevoegen';
        if (!isConnected.value) return 'Niet verbonden';
        if (!hasCalendarSelected.value) return 'Actie vereist';
        return 'Actief';
      });

      const statusClass = computed(() => {
        if (!isConnected.value) return 'outlook-card__status--idle';
        if (!hasCalendarSelected.value) return 'outlook-card__status--warning';
        return 'outlook-card__status--success';
      });

      const calendarLabel = computed(() => {
        if (!isConnected.value && props.addingAnother) {
          return `Extra ${providerLabel.value}-account`;
        }
        if (!isConnected.value) return `Nog geen ${providerLabel.value}-koppeling`;
        const settings = props.integration?.settings;
        if (settings?.calendar_name) return settings.calendar_name;
        if (settings?.user_email && settings.user_email !== title.value) {
          return settings.user_email;
        }
        return 'Agenda gekoppeld';
      });

      const handlePrimaryAction = () => {
        if (isConnected.value) emit('manage');
        else emit('connect');
      };

      const handleOpen = () => {
        handlePrimaryAction();
      };

      const handleCopyAdminConsent = (event) => {
        event?.stopPropagation?.();
        emit('copy-admin-consent');
      };

      return {
        iconSrc,
        productLabel,
        isConnected,
        hasCalendarSelected,
        title,
        description,
        actionLabel,
        statusLabel,
        statusClass,
        calendarLabel,
        handlePrimaryAction,
        handleOpen,
        handleCopyAdminConsent,
      };
    },
  };
</script>

<style scoped lang="scss">
  .outlook-card {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    background: white;
    border: 1px solid var(--slate-200);
    border-radius: 0.75rem;
    padding: 1rem;
    cursor: pointer;
    transition: transform 0.15s ease, box-shadow 0.15s ease;

    @include hover {
      transform: translateY(-1px);
      @include shadow('sm');
    }

    &--connected {
      border-color: var(--brand-200);
    }

    &__top {
      display: flex;
      justify-content: space-between;
      gap: 0.75rem;
    }

    &__brand {
      display: flex;
      gap: 0.75rem;
      min-width: 0;
      align-items: center;
    }

    &__logo {
      width: 48px;
      height: 48px;
      border-radius: 0.5rem;
      border: 1px solid var(--slate-200);
      background: var(--white);
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;

      img {
        width: 36px;
        height: 36px;
        object-fit: contain;
      }
    }

    &__heading {
      min-width: 0;
    }

    &__title {
      margin: 0 0 0.125rem;
      font-size: 1rem;
      color: var(--slate-900);
    }

    &__description {
      margin: 0;
      font-size: 0.8125rem;
      color: var(--slate-500);
      @include ellipsis(2);
    }

    &__status {
      display: inline-flex;
      align-items: center;
      border-radius: 999px;
      padding: 0.25rem 0.5rem;
      font-size: 0.6875rem;
      font-weight: 600;
      white-space: nowrap;

      &--success {
        background: var(--green-100);
        color: var(--green-700);
      }

      &--warning {
        background: var(--amber-100);
        color: var(--amber-800);
      }

      &--idle {
        background: var(--slate-100);
        color: var(--slate-600);
      }
    }

    &__banner {
      padding: 0.5rem 0.75rem;
      border-radius: 0.5rem;
      background: var(--amber-50);
      color: var(--amber-800);
      font-size: 0.8125rem;
      line-height: 1.4;
    }

    &__footer {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 0.5rem;
      margin-top: auto;
    }

    &__meta {
      font-size: 0.75rem;
      color: var(--slate-500);
      @include ellipsis(1);
      min-width: 0;
    }

    &__consent {
      display: flex;
      flex-direction: column;
      gap: 0.5rem;

      :deep(.btn) {
        width: 100%;
      }
    }

    &__consent-hint {
      margin: 0;
      font-size: 0.75rem;
      line-height: 1.4;
      color: var(--slate-500);
    }
  }
</style>
